import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, CheckCircle, XCircle, HourglassMedium, ArrowsClockwise, PencilSimple, Copy } from '@phosphor-icons/react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_PILL = {
  pending:   { label: 'Awaiting response', cls: 'bg-amber-100 text-amber-800 border-amber-300', Icon: HourglassMedium },
  accepted:  { label: 'Accepted',          cls: 'bg-green-100 text-green-800 border-green-300', Icon: CheckCircle },
  declined:  { label: 'Declined',          cls: 'bg-red-100 text-red-700 border-red-300',       Icon: XCircle },
  expired:   { label: 'No response',       cls: 'bg-gray-200 text-gray-700 border-gray-300',    Icon: XCircle },
  superseded:{ label: 'Other vendor took it', cls: 'bg-gray-100 text-text-secondary border-gray-200', Icon: XCircle },
};

const timeLeft = (expiresAtIso) => {
  if (!expiresAtIso) return null;
  const ms = new Date(expiresAtIso).getTime() - Date.now();
  if (ms <= 0) return 'expiring…';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min left`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m left`;
};

/**
 * VendorOffersBoard
 *
 * Sits inside a booking card on CustomerDashboard. After the customer hits
 * "Get Vendor Phone Numbers", this fetches the per-cart-line offer status
 * board and lets them:
 *  - see who accepted (with phone tap-to-call + copy)
 *  - swap a single line whose vendor declined / didn't respond (opens
 *    Instant Pricing in single-line swap mode)
 */
const VendorOffersBoard = ({ booking, onSwapLine, onRefetched }) => {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dispatched, setDispatched] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/customer/bookings/${booking.id}/vendor-offers`,
        { withCredentials: true }
      );
      setLines(data?.lines || []);
      setDispatched((data?.raw_count || 0) > 0);
      if (onRefetched) onRefetched();
    } catch {
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, [booking.id, onRefetched]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const handleDispatch = async () => {
    setDispatching(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/customer/vendor-offers/dispatch`,
        { booking_id: booking.id },
        { withCredentials: true }
      );
      toast.success(`${data.offers_created} vendor offer${data.offers_created === 1 ? '' : 's'} dispatched. Window: ${Math.round(data.timeout_minutes / 60)}h.`);
      await fetchOffers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to dispatch offers');
    } finally {
      setDispatching(false);
    }
  };

  // Only render in this card when the booking is the "vendors" lane and
  // hasn't been paid for yet. (Pro lane uses a different flow.)
  if (booking.lane !== 'vendors') return null;

  if (!dispatched) {
    return (
      <div className="mt-3 pt-3 border-t border-[#E8E1D5] flex flex-wrap items-center gap-2">
        <Button
          type="button"
          onClick={handleDispatch}
          disabled={dispatching}
          className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
          data-testid={`get-vendor-phones-${booking.id}`}
        >
          <Phone size={14} className="mr-1.5" weight="fill" />
          {dispatching ? 'Sending offers…' : 'Get Vendor Phone Numbers'}
        </Button>
        <p className="text-[11px] text-text-secondary">
          We'll ping each vendor — accepted vendors' phone numbers will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-[#E8E1D5] space-y-2" data-testid={`offer-board-${booking.id}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-[11px] tracking-[0.2em] uppercase font-bold text-text-secondary">
          Vendor Responses
        </h4>
        <button
          type="button"
          onClick={fetchOffers}
          disabled={loading}
          className="text-xs text-text-secondary hover:text-brand-primary inline-flex items-center gap-1"
          data-testid={`refresh-offers-${booking.id}`}
        >
          <ArrowsClockwise size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {lines.length === 0 ? (
        <p className="text-xs text-text-secondary italic">No vendor lines available.</p>
      ) : (
        <div className="space-y-2">
          {lines.map((ln) => {
            const cfg = STATUS_PILL[ln.status] || STATUS_PILL.pending;
            const Icon = cfg.Icon;
            const tl = ln.status === 'pending' ? timeLeft(ln.expires_at) : null;
            return (
              <div
                key={ln.id}
                className={`border ${ln.status === 'accepted' ? 'border-green-300 bg-green-50/40' : 'border-[#E8E1D5] bg-white'} rounded p-2 flex items-center gap-2 flex-wrap`}
                data-testid={`offer-line-${booking.id}-${ln.cart_item_id || ln.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text-primary truncate">
                    {ln.cart_item_name}
                  </div>
                  <div className="text-[10px] text-text-secondary uppercase tracking-wider">
                    {ln.category}
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${cfg.cls}`}>
                  <Icon size={12} weight="fill" /> {cfg.label}
                </span>
                {tl && <span className="text-[10px] text-text-secondary">{tl}</span>}
                {ln.status === 'accepted' && ln.vendor_phone && (
                  <div className="flex items-center gap-1 flex-wrap" data-testid={`accepted-vendor-${booking.id}-${ln.id}`}>
                    <span className="text-xs text-text-primary font-medium">{ln.vendor_name}</span>
                    <a
                      href={`tel:${ln.vendor_phone}`}
                      className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-0.5 rounded inline-flex items-center gap-1 font-mono"
                      data-testid={`call-vendor-${booking.id}-${ln.id}`}
                    >
                      <Phone size={11} /> {ln.vendor_phone}
                    </a>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(ln.vendor_phone).then(
                          () => toast.success(`Copied ${ln.vendor_phone}`),
                          () => toast.error('Copy failed'),
                        );
                      }}
                      className="text-text-secondary hover:text-brand-primary"
                      data-testid={`copy-phone-${booking.id}-${ln.id}`}
                      aria-label="Copy phone"
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                )}
                {(ln.status === 'declined' || ln.status === 'expired') && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onSwapLine && onSwapLine(booking, ln)}
                    className="text-xs rounded-none border-brand-primary text-brand-primary hover:bg-brand-primary/10"
                    data-testid={`swap-line-${booking.id}-${ln.id}`}
                  >
                    <PencilSimple size={12} className="mr-1" /> Swap
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[10px] text-text-secondary italic">
        Vendors who don't respond miss a strike — 3 misses → their calendar is auto-blocked on this event date.
      </p>
    </div>
  );
};

export default VendorOffersBoard;
